import { useState, useEffect, useRef } from 'react';
import {
  applyPromotionToCartItem,
  calculateCartTotal,
} from '../utils/productPromotion';
import { createCartLineId } from '../utils/bulkSaleUtils';

/**
 * Hook para manejar el carrito persistido en localStorage, aislado por sucursal.
 * Cada sucursal tiene su propia clave: `productsPedido_${branchId}`.
 *
 * Cuando branchId cambia (usuario vuelve al selector y elige otra sucursal):
 *  1. Se hace un flush inmediato del carrito actual a la sucursal anterior.
 *  2. Se carga el carrito de la nueva sucursal desde localStorage.
 *
 * El guardado es INMEDIATO (sin debounce) para garantizar que:
 *  - Al cambiar de sucursal rápidamente no se pierdan productos.
 *  - `clearCart()` sea seguro incluso si va seguido de window.location.reload().
 *
 * @param {number|undefined} branchId - ID de la sucursal seleccionada
 * @returns {[cart, setCart, helpers]}
 */
const mapCartWithPromotions = (items) => (items || []).map(applyPromotionToCartItem);

export const usePersistedCart = (branchId) => {
  const [cart, setCart] = useState([]);

  // Siempre refleja el valor actual de cart sin necesidad de pasar por deps de effect
  const cartRef = useRef([]);

  // ID de la sucursal actualmente cargada (usado en el effect de guardado)
  const currentBranchIdRef = useRef(undefined);

  // Flag: el cart acaba de ser cargado desde localStorage → saltar el primer guardado
  const isLoadingRef = useRef(false);

  // ─── Effect 1: mantener cartRef sincronizado ─────────────────────────────────
  // DEBE definirse antes del effect de carga para que cartRef.current
  // sea preciso cuando el effect de carga lo necesite en renders previos.
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  // ─── Effect 2: carga/descarga cuando cambia branchId ─────────────────────────
  useEffect(() => {
    const prevBranchId = currentBranchIdRef.current;

    // Flush del carrito anterior SOLO si realmente cambia la sucursal.
    // La guarda `prevBranchId !== branchId` evita que el re-run de StrictMode
    // (que asigna el mismo branchId dos veces) sobreescriba datos correctos.
    if (prevBranchId && prevBranchId !== branchId) {
      try {
        localStorage.setItem(
          `productsPedido_${prevBranchId}`,
          JSON.stringify(cartRef.current)
        );
      } catch (err) {
        console.error('Error en flush de carrito al cambiar sucursal:', err);
      }
    }

    currentBranchIdRef.current = branchId;
    isLoadingRef.current = true;

    if (!branchId) {
      setCart([]);
      return;
    }

    try {
      const stored = localStorage.getItem(`productsPedido_${branchId}`);
      setCart(stored ? mapCartWithPromotions(JSON.parse(stored)) : []);
    } catch (err) {
      console.error('Error leyendo carrito de localStorage:', err);
      setCart([]);
    }
  }, [branchId]);

  // ─── Effect 3: guardado inmediato cuando cambia el carrito ───────────────────
  // Sin debounce: el carrito es pequeño y el guardado síncrono es seguro.
  // Esto garantiza que window.location.reload() encuentre siempre datos actualizados.
  useEffect(() => {
    const activeBranchId = currentBranchIdRef.current;

    // Saltar el primer disparo tras una carga para no re-guardar datos recién leídos
    if (isLoadingRef.current) {
      isLoadingRef.current = false;
      return;
    }

    if (!activeBranchId) return;

    try {
      localStorage.setItem(
        `productsPedido_${activeBranchId}`,
        JSON.stringify(cart)
      );
    } catch (err) {
      console.error('Error guardando carrito en localStorage:', err);
    }
  }, [cart]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const helpers = {
    /**
     * Agregar producto al carrito (o incrementar cantidad si ya existe)
     */
    addBulkLine: (product, { bulkQuantity, subTotal, measurementUnit }) => {
      setCart((prevCart) =>
        mapCartWithPromotions([
          ...prevCart,
          {
            cartLineId: createCartLineId(),
            id: product.id,
            name: product.name,
            brandName: product.brandName,
            description: product.description,
            price: Number(product.price),
            cant: 1,
            bulkSale: true,
            bulkQuantity,
            subTotal,
            measurementUnit: measurementUnit || product.measurementUnit,
            activePromotion: product.activePromotion ?? null,
          },
        ])
      );
    },

    addProduct: (product) => {
      if (product.bulkSale) {
        return;
      }
      setCart(prevCart => {
        const existingIndex = prevCart.findIndex(p => p.id === product.id && !p.bulkSale);

        if (existingIndex !== -1) {
          const newCart = [...prevCart];
          newCart[existingIndex] = applyPromotionToCartItem({
            ...newCart[existingIndex],
            cant: newCart[existingIndex].cant + 1,
            price: Number(product.price),
            activePromotion: product.activePromotion ?? newCart[existingIndex].activePromotion ?? null,
          });
          return newCart;
        }

        return mapCartWithPromotions([
          ...prevCart,
          {
            id: product.id,
            name: product.name,
            brandName: product.brandName,
            description: product.description,
            price: Number(product.price),
            cant: 1,
            activePromotion: product.activePromotion ?? null,
          },
        ]);
      });
    },

    /**
     * Quitar una unidad del producto (o eliminarlo si cant === 1)
     */
    removeProduct: (productId) => {
      setCart(prevCart => {
        const bulkLines = prevCart.filter((p) => p.id === productId && p.bulkSale);
        if (bulkLines.length > 0) {
          const lastLineId = bulkLines[bulkLines.length - 1].cartLineId;
          return mapCartWithPromotions(prevCart.filter((p) => p.cartLineId !== lastLineId));
        }

        const existingIndex = prevCart.findIndex(p => p.id === productId && !p.bulkSale);
        if (existingIndex === -1) return prevCart;

        const product = prevCart[existingIndex];

        if (product.cant > 1) {
          const newCart = [...prevCart];
          newCart[existingIndex] = applyPromotionToCartItem({
            ...newCart[existingIndex],
            cant: product.cant - 1,
          });
          return newCart;
        }

        return mapCartWithPromotions(prevCart.filter(p => p.id !== productId));
      });
    },

    /**
     * Eliminar producto completamente del carrito
     */
    deleteProduct: (productId) => {
      setCart((prevCart) =>
        mapCartWithPromotions(
          prevCart.filter((p) => !(p.id === productId && !p.bulkSale))
        )
      );
    },

    deleteCartLine: (cartLineId) => {
      if (!cartLineId) return;
      setCart((prevCart) =>
        mapCartWithPromotions(prevCart.filter((p) => p.cartLineId !== cartLineId))
      );
    },

    /**
     * Actualizar cantidad específica de un producto
     */
    updateQuantity: (productId, quantity) => {
      setCart(prevCart => {
        if (quantity <= 0) {
          return mapCartWithPromotions(prevCart.filter(p => p.id !== productId));
        }

        const existingIndex = prevCart.findIndex((p) => p.id === productId && !p.bulkSale);
        if (existingIndex === -1) return prevCart;

        const newCart = [...prevCart];
        newCart[existingIndex] = applyPromotionToCartItem({
          ...newCart[existingIndex],
          cant: quantity,
        });
        return newCart;
      });
    },

    /**
     * Sincroniza precios y promociones del catálogo con ítems ya en carrito.
     * @param {Map<number, object>} catalogById
     */
    reconcileWithCatalog: (catalogById) => {
      if (!catalogById || catalogById.size === 0) return;

      setCart(prevCart => {
        if (!prevCart.length) return prevCart;

        let changed = false;
        const next = prevCart.map((item) => {
          const catalogProduct = catalogById.get(item.id);
          if (!catalogProduct) {
            const recalculated = applyPromotionToCartItem(item);
            if (JSON.stringify(recalculated.promotion) !== JSON.stringify(item.promotion)) {
              changed = true;
            }
            return recalculated;
          }

          const merged = applyPromotionToCartItem(
            item.bulkSale
              ? {
                  ...item,
                  price: Number(catalogProduct.price),
                  measurementUnit: catalogProduct.measurementUnit ?? item.measurementUnit,
                  activePromotion: catalogProduct.activePromotion ?? item.activePromotion ?? null,
                }
              : {
                  ...item,
                  price: Number(catalogProduct.price),
                  activePromotion: catalogProduct.activePromotion ?? item.activePromotion ?? null,
                }
          );

          if (
            merged.price !== item.price
            || JSON.stringify(merged.activePromotion) !== JSON.stringify(item.activePromotion)
            || JSON.stringify(merged.promotion) !== JSON.stringify(item.promotion)
          ) {
            changed = true;
          }
          return merged;
        });

        return changed ? next : prevCart;
      });
    },

    /**
     * Limpiar carrito y persistir INMEDIATAMENTE en localStorage.
     * Usar este helper antes de window.location.reload() para garantizar
     * que el localStorage quede con el carrito vacío antes de la recarga.
     */
    clearCart: () => {
      const activeBranchId = currentBranchIdRef.current;
      if (activeBranchId) {
        try {
          localStorage.setItem(`productsPedido_${activeBranchId}`, JSON.stringify([]));
        } catch (err) {
          console.error('Error limpiando carrito en localStorage:', err);
        }
      }
      setCart([]);
    },

    /**
     * Obtener cantidad de un producto específico
     */
    getProductQuantity: (productId) => {
      const product = cart.find(p => p.id === productId);
      return product ? product.cant : 0;
    },

    /**
     * Calcular total del carrito (con promociones aplicadas)
     */
    calculateTotal: () => calculateCartTotal(cart),

    /**
     * Obtener cantidad total de items
     */
    getTotalItems: () => {
      return cart.reduce((total, p) => total + (p.bulkSale ? 1 : p.cant), 0);
    },
  };

  return [cart, setCart, helpers];
};
